// Public legal pages — reachable at /privacy, /terms, and /disclaimer
// WITHOUT entering a shift or acknowledging the in-app legal wall.
// These URLs exist specifically to satisfy App Store Connect and Google
// Play Console submission requirements.
//
// They:
//   - Render pure HTML (crawlable, printable, no app chrome)
//   - Do NOT touch localStorage, do NOT render the ShiftProvider
//   - Have proper <h1>/<h2> headings for SEO
//   - Include a tiny back-link to the main app

import React, { useEffect } from 'react';
import {
  MEDICAL_DISCLAIMER,
  TERMS_OF_SERVICE,
  PRIVACY_POLICY,
  LEGAL_VERSION,
  LEGAL_EFFECTIVE_DATE,
} from '../data/legal';

const DOCS = {
  privacy: PRIVACY_POLICY,
  terms: TERMS_OF_SERVICE,
  disclaimer: MEDICAL_DISCLAIMER,
};

export default function LegalPage({ kind }) {
  const doc = DOCS[kind];

  useEffect(() => {
    if (doc?.title) document.title = `${doc.title} · ShiftReady`;
  }, [doc]);

  if (!doc) return null;

  return (
    <main
      data-testid={`legal-page-${kind}`}
      style={{
        minHeight: '100vh',
        background: '#F5F5F1',
        color: '#0B0F14',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        padding: '48px 20px 80px',
        lineHeight: 1.55,
      }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <a
          href="/"
          data-testid="legal-back-home"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: '#0B7285',
            textDecoration: 'none',
            fontSize: 14,
            fontWeight: 700,
            marginBottom: 32,
          }}
        >
          ← Back to ShiftReady
        </a>

        <h1
          style={{
            fontSize: 36,
            fontWeight: 900,
            letterSpacing: '-0.8px',
            marginBottom: 8,
          }}
        >
          {doc.title}
        </h1>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 32 }}>
          Effective {LEGAL_EFFECTIVE_DATE} · Version {LEGAL_VERSION}
        </p>

        {doc.sections.map((s, i) => (
          <section key={i} style={{ marginBottom: 24 }}>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 800,
                marginBottom: 8,
                letterSpacing: '-0.2px',
              }}
            >
              {s.heading}
            </h2>
            <p style={{ fontSize: 15, color: '#3F4A55', whiteSpace: 'pre-wrap' }}>
              {s.body}
            </p>
          </section>
        ))}

        <hr style={{ border: 0, borderTop: '1px solid #E5E3DE', margin: '40px 0 20px' }} />

        <footer style={{ fontSize: 12, color: '#6B7280' }}>
          <p style={{ marginBottom: 8 }}>
            ShiftReady is operated by ShiftReady LLC. Contact:{' '}
            <a
              href="mailto:support@shiftreadyllc.com"
              style={{ color: '#0B7285', textDecoration: 'underline' }}
            >
              support@shiftreadyllc.com
            </a>
          </p>
          <p>
            <a href="/privacy" style={{ color: '#0B7285', marginRight: 12 }}>
              Privacy Policy
            </a>
            <a href="/terms" style={{ color: '#0B7285', marginRight: 12 }}>
              Terms of Service
            </a>
            <a href="/disclaimer" style={{ color: '#0B7285' }}>
              Medical Disclaimer
            </a>
          </p>
        </footer>
      </div>
    </main>
  );
}
